/**
 * ReportHeadersPanel — reusable inline preview panel showing the expected
 * column headers for the currently-selected Report Type.
 *
 * Consumed by:
 *   - Upload Center page (next to the Report Type dropdown)
 *   - Any future upload modal (drop it in, pass the same props)
 *
 * Props:
 *   channel, adType, adProduct, reportType — the current selection
 *   open      — controlled open/close (boolean)
 *   onClose   — callback to close the panel
 *
 * The panel updates live whenever selection props change (the parent just
 * re-renders with new props; internal state resets on each render via a
 * memoized spec lookup).
 */
import { useMemo } from 'react';
import { Button } from './ui/button';
import { X, Download, ClipboardCopy, FileText, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { getReportHeadersSpec, serializeHeadersCsv } from '../lib/reportHeadersSpec';

/**
 * Convert a 0-based column index to Excel-style letters (0→A, 25→Z, 26→AA…).
 */
function colLetter(n) {
  let s = '';
  n = n + 1; // Excel is 1-indexed
  while (n > 0) {
    const r = (n - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

export default function ReportHeadersPanel({
  channel = '',
  adType = '',
  adProduct = '',
  reportType = '',
  open = false,
  onClose = () => {},
}) {
  const spec = useMemo(
    () => getReportHeadersSpec({ channel, adType, adProduct, reportType }),
    [channel, adType, adProduct, reportType]
  );

  if (!open) return null;

  const handleDownloadHeadersCsv = () => {
    if (!spec || !spec.headers?.length) {
      toast.error('No headers to download for this selection');
      return;
    }
    const csv = serializeHeadersCsv(spec);
    const safe = [channel, adProduct, reportType]
      .filter(Boolean)
      .join('_')
      .replace(/[^A-Za-z0-9_-]+/g, '_');
    const today = new Date().toISOString().slice(0, 10);
    const fileName = `Headers_${safe || 'Report'}_${today}.csv`;

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${fileName}`);
  };

  const handleCopyHeaders = async () => {
    if (!spec || !spec.headers?.length) return;
    const line = spec.headers.map(h => h.name).join(', ');
    try {
      await navigator.clipboard.writeText(line);
      toast.success('Headers copied to clipboard');
    } catch (e) {
      toast.error('Copy failed — download the CSV instead');
    }
  };

  return (
    <div
      className="mt-4 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden"
      data-testid="report-headers-panel"
    >
      {/* Header strip */}
      <div className="flex items-start justify-between px-5 py-4 bg-gradient-to-r from-[#32B4AC]/5 to-transparent border-b border-gray-100">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-xs font-semibold text-[#32B4AC] uppercase tracking-wider">
            <FileText className="w-3.5 h-3.5" />
            Report Format Preview
          </div>
          <div className="mt-1.5 flex flex-wrap gap-1.5 items-center">
            <span className="px-2 py-0.5 bg-[#32B4AC]/10 text-[#32B4AC] rounded font-medium text-xs">
              {channel || '—'}
            </span>
            <span className="text-gray-300 text-xs">/</span>
            <span className="px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded font-medium text-xs">
              {adType || '—'}
            </span>
            <span className="text-gray-300 text-xs">/</span>
            <span className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded font-medium text-xs">
              {adProduct || '—'}
            </span>
            <span className="text-gray-300 text-xs">/</span>
            <span className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded font-medium text-xs">
              {reportType || '—'}
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-700 transition-colors -mr-1"
          aria-label="Close preview"
          data-testid="close-headers-panel"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Body */}
      {!spec ? (
        <div className="px-5 py-8 text-center" data-testid="headers-empty-state">
          <AlertCircle className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">
            Header spec not documented for this combination yet.
          </p>
          <p className="text-[11px] text-gray-400 mt-1">
            Ping the platform team to add it — parsers may still work if the file&apos;s headers match the pattern.
          </p>
        </div>
      ) : (
        <>
          {/* Description */}
          <div className="px-5 pt-4 pb-2 space-y-1.5">
            {spec.description && (
              <p className="text-sm text-gray-700 leading-relaxed">{spec.description}</p>
            )}
            {spec.source && (
              <p className="text-[11px] text-gray-500">
                <span className="font-semibold text-gray-600">Source:</span> {spec.source}
              </p>
            )}
          </div>

          {/* Headers table — Excel-like horizontal layout: column letters
              across the top (A, B, C…), header names in row 1, example
              values in row 2. Required columns get a subtle amber tick. */}
          {spec.headers.length === 0 ? (
            <div className="px-5 py-6 text-center border-t border-gray-100">
              <p className="text-sm text-gray-500 italic">No headers documented — parser not wired yet.</p>
            </div>
          ) : (
            <div className="border-t border-gray-100 bg-white px-5 py-4">
              <div className="overflow-x-auto rounded-md border border-[#D0D7DE] shadow-sm" data-testid="headers-table">
                <table className="border-collapse font-sans" style={{ minWidth: '100%' }}>
                  {/* Excel column-letter strip (A, B, C…) */}
                  <thead>
                    <tr>
                      {/* Row-number corner cell */}
                      <th className="sticky left-0 z-10 w-10 min-w-[40px] bg-[#F0F0F0] border border-[#D0D7DE] text-[11px] font-normal text-gray-500"></th>
                      {spec.headers.map((h, i) => (
                        <th
                          key={h.name + '-col-' + i}
                          className="border border-[#D0D7DE] bg-[#F0F0F0] text-[11px] font-normal text-gray-600 text-center tabular-nums"
                          style={{ minWidth: 180, height: 22, padding: '0 6px' }}
                        >
                          {colLetter(i)}
                        </th>
                      ))}
                    </tr>
                    {/* Row 1 — the actual header names (Excel style bold) */}
                    <tr>
                      <td className="sticky left-0 z-10 w-10 min-w-[40px] bg-[#F0F0F0] border border-[#D0D7DE] text-[11px] font-normal text-gray-500 text-center tabular-nums">
                        1
                      </td>
                      {spec.headers.map((h, i) => (
                        <td
                          key={h.name + '-name-' + i}
                          className={`border border-[#D0D7DE] px-2 py-1.5 text-[13px] font-semibold text-[#0F0F0F] align-top ${
                            h.required ? 'bg-[#FFF8E5]' : 'bg-white'
                          }`}
                          style={{ minWidth: 180, verticalAlign: 'top' }}
                          title={h.description || ''}
                        >
                          <div className="flex items-start gap-1 leading-tight">
                            <span className="font-mono whitespace-nowrap overflow-hidden text-ellipsis">{h.name}</span>
                            {h.required && (
                              <span className="text-[9px] font-bold text-amber-600 leading-none mt-0.5" title="Required">
                                *
                              </span>
                            )}
                          </div>
                        </td>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Row 2 — example values in monospace grey (mimics Excel's sample-data hint row) */}
                    <tr>
                      <td className="sticky left-0 z-10 w-10 min-w-[40px] bg-[#F0F0F0] border border-[#D0D7DE] text-[11px] font-normal text-gray-500 text-center tabular-nums">
                        2
                      </td>
                      {spec.headers.map((h, i) => (
                        <td
                          key={h.name + '-ex-' + i}
                          className="border border-[#D0D7DE] px-2 py-1.5 text-[12px] font-mono text-gray-500 bg-white align-top"
                          style={{ minWidth: 180 }}
                        >
                          {h.example || '—'}
                        </td>
                      ))}
                    </tr>
                    {/* Row 3 — inline description in italic grey */}
                    <tr>
                      <td className="sticky left-0 z-10 w-10 min-w-[40px] bg-[#F0F0F0] border border-[#D0D7DE] text-[11px] font-normal text-gray-500 text-center tabular-nums">
                        3
                      </td>
                      {spec.headers.map((h, i) => (
                        <td
                          key={h.name + '-desc-' + i}
                          className="border border-[#D0D7DE] px-2 py-1.5 text-[11px] italic text-gray-400 bg-[#FAFBFC] align-top leading-snug"
                          style={{ minWidth: 180 }}
                        >
                          {h.description || '—'}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-gray-500 mt-2">
                <span className="inline-block w-2.5 h-2.5 bg-[#FFF8E5] border border-[#D0D7DE] rounded-sm align-middle mr-1"></span>
                Amber-tinted cells with <span className="text-amber-600 font-bold">*</span> are required · Row 1 = header text (paste this into your file&apos;s first row) · Row 2 = example value · Row 3 = description
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-2 px-5 py-3 border-t border-gray-100 bg-gray-50/40">
            <div className="text-[11px] text-gray-500 mr-auto">
              <span className="font-semibold text-gray-700">{spec.headers.length}</span>
              {' '}column{spec.headers.length === 1 ? '' : 's'} expected
              {' · '}
              <span className="text-rose-600 font-semibold">
                {spec.headers.filter(h => h.required).length} required
              </span>
            </div>
            {spec.headers.length > 0 && (
              <>
                <button
                  type="button"
                  onClick={handleCopyHeaders}
                  className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium border border-gray-200 rounded-md bg-white hover:bg-gray-50 text-gray-700 transition-colors"
                  data-testid="copy-headers-btn"
                >
                  <ClipboardCopy className="w-3.5 h-3.5" />
                  Copy Headers
                </button>
                <Button
                  size="sm"
                  onClick={handleDownloadHeadersCsv}
                  className="h-8 bg-[#32B4AC] hover:bg-[#2a9d95] text-white"
                  data-testid="download-headers-csv-btn"
                >
                  <Download className="w-3.5 h-3.5 mr-1.5" />
                  Download Headers Only (.csv)
                </Button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
